import React, { useState, useRef, useEffect } from 'react';

export interface CitationData {
  id: string;
  source: string;
  url?: string;
  description?: string;
}

interface CitationProps {
  citation: CitationData;
  children: React.ReactNode;
}

export const Citation: React.FC<CitationProps> = ({ citation, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <span className="relative inline">
      <span
        ref={triggerRef}
        className="cursor-help border-b border-dotted border-gray-400 hover:border-gray-500 hover:text-gray-600 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {children}
      </span>
      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute z-50 bottom-full left-1/2 -trangray-x-1/2 mb-2 w-64 p-3 bg-white  shadow-xl border border-gray-200 text-sm"
        >
          <div className="absolute bottom-0 left-1/2 -trangray-x-1/2 trangray-y-full">
            <div className="w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45 -trangray-y-1.5" />
          </div>
          <p className="font-medium text-gray-900 mb-1">{citation.source}</p>
          {citation.description && (
            <p className="text-gray-600 text-xs mb-2">{citation.description}</p>
          )}
          {citation.url && (
            <a
              href={citation.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-800 text-xs flex items-center gap-1"
            >
              View source
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>
      )}
    </span>
  );
};

interface CitationRefProps {
  id: number;
}

export const CitationRef: React.FC<CitationRefProps> = ({ id }) => {
  return (
    <sup className="text-gray-600 hover:text-gray-800 cursor-pointer text-xs font-medium ml-0.5">
      [{id}]
    </sup>
  );
};

export default Citation;
